import React, { useState, useEffect, useCallback } from 'react';
import {
  Row, Col, Card, Spinner, ProgressBar, Badge, Button,
  Alert, OverlayTrigger, Tooltip, Tab, Tabs
} from 'react-bootstrap';
import {
  FaDatabase, FaCheckCircle, FaExclamationTriangle, FaBrain,
  FaLayerGroup, FaFlag, FaStar, FaClock, FaArrowUp, FaArrowDown,
  FaShieldAlt, FaHeart, FaChartLine, FaBalanceScale,
  FaProjectDiagram, FaFingerprint, FaEye, FaExchangeAlt,
  FaSyncAlt, FaThermometerHalf, FaTimesCircle, FaTags,
  FaChartBar, FaHistory, FaInfoCircle, FaSitemap
} from 'react-icons/fa';
import knowledgeBaseApi from '../../services/knowledgeBaseApi';

// ────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────
const scoreColor = (val, max = 1) => {
  const pct = (val / max) * 100;
  if (pct >= 75) return '#22c55e';
  if (pct >= 50) return '#eab308';
  if (pct >= 25) return '#f97316';
  return '#ef4444';
};

const formatNum = (n) => {
  if (n === null || n === undefined) return '—';
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'k';
  return String(n);
};

const formatPct = (v, decimals = 0) => {
  if (v === null || v === undefined) return '—';
  return `${(v * 100).toFixed(decimals)}%`;
};

const formatDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
  catch { return d; }
};

// ────────────────────────────────────────────────────────
// Micro-components
// ────────────────────────────────────────────────────────
const MetricTile = ({ label, value, icon: Icon, color, sub, trend }) => (
  <div className="db-metric-tile">
    <div className="db-metric-icon" style={{ color: color || 'var(--electric-cyan)' }}>
      {Icon && <Icon />}
    </div>
    <div className="db-metric-info">
      <div className="db-metric-value" style={trend < 0 ? { color: '#ef4444' } : undefined}>{value}</div>
      <div className="db-metric-label">{label}</div>
      {sub && <div className="db-metric-sub">{sub}</div>}
    </div>
    {trend !== undefined && trend !== null && (
      <div className={`db-metric-trend ${trend >= 0 ? 'positive' : 'negative'}`}>
        {trend >= 0 ? <FaArrowUp /> : <FaArrowDown />}
        {Math.abs(trend).toFixed(1)}%
      </div>
    )}
  </div>
);

const PercentileBar = ({ label, p25, median, p75, color }) => (
  <div className="db-pct-bar">
    <div className="db-pct-label">{label}</div>
    <div className="db-pct-track">
      <div className="db-pct-range" style={{ left: `${Math.max(0, (p25 + 1) / 2 * 100)}%`, width: `${Math.max(1, ((p75 - p25) / 2) * 100)}%`, background: color + '30' }} />
      <div className="db-pct-median" style={{ left: `${Math.max(0, (median + 1) / 2 * 100)}%`, background: color }} />
    </div>
    <div className="db-pct-values">
      <span>P25: {p25?.toFixed(3)}</span>
      <span className="db-pct-mid">Median: {median?.toFixed(3)}</span>
      <span>P75: {p75?.toFixed(3)}</span>
    </div>
  </div>
);

const MiniBar = ({ label, value, max, color }) => {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="db-minibar">
      <div className="db-minibar-head">
        <span className="db-minibar-label">{label}</span>
        <span className="db-minibar-value">{value}</span>
      </div>
      <div className="db-minibar-track">
        <div className="db-minibar-fill" style={{ width: `${Math.min(pct, 100)}%`, background: color || 'var(--electric-cyan)' }} />
      </div>
    </div>
  );
};

const AlertBadge = ({ alert }) => {
  const colors = { critical: '#ef4444', warning: '#eab308', info: '#3b82f6' };
  const bgs = { critical: 'rgba(239,68,68,0.12)', warning: 'rgba(234,179,8,0.1)', info: 'rgba(59,130,246,0.1)' };
  return (
    <div className="db-alert-badge" style={{ background: bgs[alert.level], borderColor: colors[alert.level] + '40' }}>
      <FaExclamationTriangle style={{ color: colors[alert.level] }} />
      <span className="db-alert-msg">{alert.message}</span>
      <Badge className="db-alert-val" style={{ background: colors[alert.level] + '20', color: colors[alert.level] }}>
        {alert.value}
      </Badge>
    </div>
  );
};

const TrendBar = ({ data, valueKey, labelKey, height = 100 }) => {
  if (!data || data.length === 0) return <div className="db-empty-sm">No trend data</div>;
  const maxVal = Math.max(...data.map(d => d[valueKey] || 0), 1);
  return (
    <div className="db-trend-chart" style={{ height }}>
      {data.map((d, i) => (
        <OverlayTrigger key={i} placement="top" overlay={<Tooltip>{d[labelKey] ? formatDate(d[labelKey]) : `#${i + 1}`}: {d[valueKey]}</Tooltip>}>
          <div className="db-trend-col">
            <div className="db-trend-bar" style={{ height: `${((d[valueKey] || 0) / maxVal) * 100}%` }} />
          </div>
        </OverlayTrigger>
      ))}
    </div>
  );
};

// ══════════════════════════════════════════════════════════
// MAIN DASHBOARD COMPONENT
// ══════════════════════════════════════════════════════════
const KnowledgeDashboard = ({ setError, setSuccess, selectedAgent }) => {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [healthCheck, setHealthCheck] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [activeDeep, setActiveDeep] = useState('overview');

  // Deep-dive lazy data
  const [calibrationData, setCalibrationData] = useState(null);
  const [calibrationLoading, setCalibrationLoading] = useState(false);
  const [sourceData, setSourceData] = useState(null);
  const [sourceLoading, setSourceLoading] = useState(false);
  const [scoreEvoData, setScoreEvoData] = useState(null);
  const [scoreEvoLoading, setScoreEvoLoading] = useState(false);
  const [classData, setClassData] = useState(null);
  const [classLoading, setClassLoading] = useState(false);
  const [trendsData, setTrendsData] = useState(null);
  const [trendsLoading, setTrendsLoading] = useState(false);
  const [nodeData, setNodeData] = useState(null);
  const [nodeLoading, setNodeLoading] = useState(false);
  const [growthData, setGrowthData] = useState(null);
  const [growthLoading, setGrowthLoading] = useState(false);

  const agentId = selectedAgent === 'all' ? undefined : parseInt(selectedAgent);

  // ── Main dashboard load ──
  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const data = await knowledgeBaseApi.analytics.getDashboard(agentId || 1, 30);
      setDashboard(data?.dashboard || data || null);
    } catch (err) {
      console.error('Dashboard error:', err);
      setError?.('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [agentId, setError]);

  const fetchHealthCheck = useCallback(async () => {
    setHealthLoading(true);
    try {
      const data = await knowledgeBaseApi.analytics.getHealthCheck();
      setHealthCheck(data || null);
    } catch (err) { console.error('Health check error:', err); }
    finally { setHealthLoading(false); }
  }, []);

  useEffect(() => { fetchDashboard(); fetchHealthCheck(); }, [fetchDashboard, fetchHealthCheck]);

  // ── Lazy loaders for deep tabs ──
  const loadCalibration = async () => {
    if (calibrationData) return;
    setCalibrationLoading(true);
    try { const d = await knowledgeBaseApi.analytics.getCalibrationAnalytics(agentId, 30); setCalibrationData(d); }
    catch (e) { console.error(e); } finally { setCalibrationLoading(false); }
  };

  const loadSources = async () => {
    if (sourceData) return;
    setSourceLoading(true);
    try { const d = await knowledgeBaseApi.analytics.getSourceAnalytics(agentId, 90); setSourceData(d); }
    catch (e) { console.error(e); } finally { setSourceLoading(false); }
  };

  const loadScoreEvo = async () => {
    if (scoreEvoData) return;
    setScoreEvoLoading(true);
    try { const d = await knowledgeBaseApi.analytics.getScoreEvolution(agentId, 90, 'week'); setScoreEvoData(d); }
    catch (e) { console.error(e); } finally { setScoreEvoLoading(false); }
  };

  const loadClassifications = async () => {
    if (classData) return;
    setClassLoading(true);
    try { const d = await knowledgeBaseApi.analytics.getClassificationBreakdown(agentId); setClassData(d); }
    catch (e) { console.error(e); } finally { setClassLoading(false); }
  };

  const loadTrends = async () => {
    if (trendsData) return;
    setTrendsLoading(true);
    try { const d = await knowledgeBaseApi.analytics.getTrends(agentId, 30, 'day'); setTrendsData(d); }
    catch (e) { console.error(e); } finally { setTrendsLoading(false); }
  };

  const loadNodes = async () => {
    if (nodeData) return;
    setNodeLoading(true);
    try { const d = await knowledgeBaseApi.analytics.getNodeEvolution(90); setNodeData(d); }
    catch (e) { console.error(e); } finally { setNodeLoading(false); }
  };

  const loadGrowth = async () => {
    if (growthData) return;
    setGrowthLoading(true);
    try { const d = await knowledgeBaseApi.analytics.getGrowthProjection(agentId, 90); setGrowthData(d); }
    catch (e) { console.error(e); } finally { setGrowthLoading(false); }
  };

  // Tab change triggers lazy load
  useEffect(() => {
    if (activeDeep === 'calibration') loadCalibration();
    if (activeDeep === 'sources') loadSources();
    if (activeDeep === 'scores') loadScoreEvo();
    if (activeDeep === 'classifications') loadClassifications();
    if (activeDeep === 'trends') loadTrends();
    if (activeDeep === 'nodes') loadNodes();
    if (activeDeep === 'growth') loadGrowth();
  }, [activeDeep]);

  // ── Loading / Error states ──
  if (loading) {
    return (
      <div className="db-loading">
        <Spinner animation="border" variant="info" />
        <span>Loading analytics dashboard…</span>
      </div>
    );
  }

  if (!dashboard) {
    return <div className="db-empty">Dashboard data unavailable. <Button size="sm" variant="outline-info" onClick={fetchDashboard} className="ms-2"><FaSyncAlt /> Retry</Button></div>;
  }

  const {
    overview = {}, learnings_summary = {}, classification_summary = [],
    cluster_summary = {}, quality_summary = {}, conflict_summary = {},
    source_summary = {}, calibration_summary = {}, pattern_summary = {},
    score_health = {}, recent_activity = []
  } = dashboard;

  const totalNeg = overview.negative_credibility_count || 0;

  // ══════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════
  return (
    <div className="db-container">
      {/* ── Health Alert Banner ── */}
      {healthCheck && healthCheck.alerts && healthCheck.alerts.length > 0 && (
        <div className="db-health-banner">
          <div className="db-health-banner-head">
            <FaHeart style={{ color: scoreColor(healthCheck.health_score / 100) }} />
            <span className="db-health-score" style={{ color: scoreColor(healthCheck.health_score / 100) }}>
              {healthCheck.health_score}
            </span>
            <span className="db-health-status">{healthCheck.status}</span>
            <span className="db-health-time">{healthCheck.alerts.length} alerts</span>
          </div>
          <div className="db-health-alerts">
            {healthCheck.alerts.map((a, i) => <AlertBadge key={i} alert={a} />)}
          </div>
        </div>
      )}

      {/* ── Top Metrics Row ── */}
      <div className="db-metrics-row">
        <MetricTile label="Total Learnings" value={formatNum(overview.total_learnings)} icon={FaDatabase} color="#3b82f6" sub={`${overview.active_learnings || 0} active · ${overview.archived_learnings || 0} archived`} />
        <MetricTile label="New (Period)" value={formatNum(overview.new_learnings_period)} icon={FaClock} color="#22c55e" sub={`Last ${dashboard.time_period_days || 30} days`} />
        <MetricTile label="Review Needed" value={overview.flagged_for_review || 0} icon={FaFlag} color="#eab308" />
        <MetricTile label="Clusters" value={cluster_summary.total_clusters || 0} icon={FaProjectDiagram} color="#8b5cf6" sub={`Avg ${cluster_summary.avg_members?.toFixed(0) || 0} members`} />
        <MetricTile label="Neg Credibility" value={totalNeg} icon={FaTimesCircle} color={totalNeg > 0 ? '#ef4444' : '#64748b'} />
        <MetricTile label="Sources" value={source_summary.total_sources || 0} icon={FaSitemap} color="#06b6d4" sub={`${source_summary.high_trust_sources || 0} high trust`} />
      </div>

      {/* ── Score Health (Percentiles) ── */}
      {score_health && (score_health.trust || score_health.credibility) && (
        <Card className="db-card">
          <Card.Header className="db-card-header">
            <FaThermometerHalf className="me-2 text-info" /> Score Health (Percentiles)
          </Card.Header>
          <Card.Body className="db-card-body">
            <Row className="g-3">
              <Col md={4}>
                <PercentileBar label="Trust" p25={score_health.trust?.p25} median={score_health.trust?.median} p75={score_health.trust?.p75} color="#22c55e" />
              </Col>
              <Col md={4}>
                <PercentileBar label="Credibility" p25={score_health.credibility?.p25} median={score_health.credibility?.median} p75={score_health.credibility?.p75} color="#3b82f6" />
              </Col>
              <Col md={4}>
                <PercentileBar label="Importance" p25={score_health.importance?.p25} median={score_health.importance?.median} p75={score_health.importance?.p75} color="#eab308" />
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* ── Summary Cards Row ── */}
      <Row className="g-3 mb-3">
        {/* Quality */}
        <Col md={3}>
          <Card className="db-card h-100">
            <Card.Header className="db-card-header"><FaStar className="me-2 text-warning" /> Quality</Card.Header>
            <Card.Body className="db-card-body">
              <div className="db-big-number" style={{ color: scoreColor(quality_summary.avg_quality || 0) }}>
                {formatPct(quality_summary.avg_quality)}
              </div>
              <div className="db-big-label">Overall Quality</div>
              <div className="db-stat-row"><span>With Metrics</span><strong>{quality_summary.total_with_metrics || 0}</strong></div>
              <div className="db-stat-row"><span>High Quality</span><strong className="text-success">{quality_summary.high_quality || 0}</strong></div>
              <div className="db-stat-row"><span>Low Quality</span><strong className="text-danger">{quality_summary.low_quality || 0}</strong></div>
              <div className="db-stat-row"><span>Success Rate</span><strong>{formatPct(quality_summary.success_rate)}</strong></div>
            </Card.Body>
          </Card>
        </Col>

        {/* Conflicts */}
        <Col md={3}>
          <Card className="db-card h-100">
            <Card.Header className="db-card-header"><FaExclamationTriangle className="me-2 text-warning" /> Conflicts</Card.Header>
            <Card.Body className="db-card-body">
              <div className="db-big-number text-warning">{conflict_summary.total || 0}</div>
              <div className="db-big-label">Total Conflicts</div>
              <div className="db-stat-row"><span>Unresolved</span><strong className="text-danger">{conflict_summary.unresolved || 0}</strong></div>
              <div className="db-stat-row"><span>Auto-resolved</span><strong className="text-success">{conflict_summary.auto_resolved || 0}</strong></div>
              <div className="db-stat-row"><span>Needs Review</span><strong>{conflict_summary.requiring_review || 0}</strong></div>
              <div className="db-stat-row"><span>New (Period)</span><strong>{conflict_summary.new_in_period || 0}</strong></div>
              {conflict_summary.avg_resolution_hours && (
                <div className="db-stat-row"><span>Avg Resolution</span><strong>{conflict_summary.avg_resolution_hours}h</strong></div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Calibration */}
        <Col md={3}>
          <Card className="db-card h-100">
            <Card.Header className="db-card-header"><FaBalanceScale className="me-2 text-info" /> Calibration</Card.Header>
            <Card.Body className="db-card-body">
              <div className="db-big-number text-info">{calibration_summary.total_calibrations || 0}</div>
              <div className="db-big-label">Total Calibrations</div>
              <div className="db-stat-row"><span>In Period</span><strong>{calibration_summary.calibrations_in_period || 0}</strong></div>
              <div className="db-stat-row"><span>Avg Peers</span><strong>{calibration_summary.avg_peers_evaluated?.toFixed(1) || '—'}</strong></div>
              <div className="db-stat-row">
                <span>Avg Cred Change</span>
                <strong style={{ color: (calibration_summary.avg_credibility_change || 0) >= 0 ? '#22c55e' : '#ef4444' }}>
                  {calibration_summary.avg_credibility_change?.toFixed(4) || '—'}
                </strong>
              </div>
              <div className="db-stat-row"><span>With Conflicts</span><strong>{calibration_summary.calibrations_with_conflicts || 0}</strong></div>
            </Card.Body>
          </Card>
        </Col>

        {/* Sources */}
        <Col md={3}>
          <Card className="db-card h-100">
            <Card.Header className="db-card-header"><FaSitemap className="me-2 text-primary" /> Sources</Card.Header>
            <Card.Body className="db-card-body">
              <div className="db-big-number text-primary">{source_summary.total_sources || 0}</div>
              <div className="db-big-label">Active Sources</div>
              <div className="db-stat-row"><span>Avg Trust</span><strong>{formatPct(source_summary.avg_trust)}</strong></div>
              <div className="db-stat-row"><span>High Trust</span><strong className="text-success">{source_summary.high_trust_sources || 0}</strong></div>
              <div className="db-stat-row"><span>Low Trust</span><strong className="text-danger">{source_summary.low_trust_sources || 0}</strong></div>
              <div className="db-stat-row"><span>Confirmation Rate</span><strong>{formatPct(source_summary.confirmation_rate)}</strong></div>
              <div className="db-stat-row"><span>Submissions</span><strong>{formatNum(source_summary.total_submissions)}</strong></div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ── Classification Summary ── */}
      {classification_summary && classification_summary.length > 0 && (
        <Card className="db-card">
          <Card.Header className="db-card-header"><FaTags className="me-2" /> Classification Summary</Card.Header>
          <Card.Body className="db-card-body">
            <div className="db-class-grid">
              {classification_summary.map((c, i) => {
                const maxCount = Math.max(...classification_summary.map(x => x.count), 1);
                return (
                  <div key={i} className="db-class-item">
                    <div className="db-class-head">
                      <span className="db-class-name">{c.classification}</span>
                      <span className="db-class-count">{c.count}</span>
                    </div>
                    <div className="db-class-bar-track">
                      <div className="db-class-bar-fill" style={{ width: `${(c.count / maxCount) * 100}%` }} />
                    </div>
                    <div className="db-class-scores">
                      <span>Cred: {c.avg_credibility?.toFixed(3)}</span>
                      <span>Trust: {formatPct(c.avg_trust)}</span>
                      {c.negative_credibility > 0 && <span className="text-danger">{c.negative_credibility} neg</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* ── Recent Activity + Learning Types ── */}
      <Row className="g-3 mb-3">
        <Col lg={8}>
          <Card className="db-card">
            <Card.Header className="db-card-header"><FaClock className="me-2" /> Recent Activity</Card.Header>
            <Card.Body className="db-card-body p-0">
              {recent_activity && recent_activity.length > 0 ? (
                <div className="db-activity-list">
                  {recent_activity.map((a, i) => (
                    <div key={i} className="db-activity-row">
                      <div className="db-activity-main">
                        <div className="db-activity-badges">
                          <Badge className="db-type-badge">{a.type || 'unknown'}</Badge>
                          {a.classification && <Badge className="db-class-badge">{a.classification}</Badge>}
                        </div>
                        <div className="db-activity-preview">{a.preview || 'No preview'}</div>
                      </div>
                      <div className="db-activity-meta">
                        {a.source && <span className="db-activity-source">{a.source}</span>}
                        <span className="db-activity-cred" style={{ color: scoreColor(Math.max(0, a.credibility || 0)) }}>
                          {a.credibility?.toFixed(3)}
                        </span>
                        <span className="db-activity-usage"><FaEye /> {a.usage || 0}</span>
                        <span className="db-activity-date">{formatDate(a.created_at)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="db-empty-sm">No recent activity</div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="db-card h-100">
            <Card.Header className="db-card-header"><FaLayerGroup className="me-2" /> By Type & Scope</Card.Header>
            <Card.Body className="db-card-body p-0">
              {learnings_summary.by_type_and_scope && learnings_summary.by_type_and_scope.length > 0 ? (
                <div className="db-type-list">
                  {learnings_summary.by_type_and_scope.slice(0, 8).map((item, i) => (
                    <div key={i} className="db-type-row">
                      <div className="db-type-info">
                        <span className="db-type-name">{item.learning_type}</span>
                        <span className="db-type-scope">{item.scope}</span>
                      </div>
                      <div className="db-type-stats">
                        <Badge className="db-type-count">{item.count}</Badge>
                        <span className="db-type-score">Cred: {item.avg_credibility?.toFixed(2)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="db-empty-sm">No data</div>
              )}

              {/* Scope totals */}
              {learnings_summary.by_scope_totals && Object.keys(learnings_summary.by_scope_totals).length > 0 && (
                <div className="db-scope-totals">
                  {Object.entries(learnings_summary.by_scope_totals).map(([scope, count]) => (
                    <div key={scope} className="db-scope-chip">
                      <span>{scope}</span>
                      <strong>{count}</strong>
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ── Deep Dive Tabs ── */}
      <Card className="db-card">
        <Card.Header className="db-card-header">
          <FaChartBar className="me-2" /> Deep Analytics
          <Button variant="outline-info" size="sm" className="ms-auto" onClick={() => { setCalibrationData(null); setSourceData(null); setScoreEvoData(null); setClassData(null); setTrendsData(null); setNodeData(null); setGrowthData(null); }}>
            <FaSyncAlt className="me-1" /> Reset Cache
          </Button>
        </Card.Header>
        <Tabs activeKey={activeDeep} onSelect={setActiveDeep} className="db-deep-tabs">

          {/* TRENDS */}
          <Tab eventKey="trends" title={<><FaChartLine className="me-1" /> Trends</>}>
            <div className="db-deep-content">
              {trendsLoading ? <div className="db-loading-sm"><Spinner size="sm" variant="info" /> Loading…</div> : trendsData ? (
                <>
                  <h6 className="db-deep-title">Daily Learning Creation</h6>
                  <TrendBar data={trendsData.trends} valueKey="new_learnings" labelKey="period" height={120} />
                  <div className="db-trend-table">
                    {trendsData.trends?.slice(-7).map((t, i) => (
                      <div key={i} className="db-trend-row">
                        <span>{formatDate(t.period)}</span>
                        <span><strong>{t.new_learnings}</strong> new</span>
                        <span>Trust: {formatPct(t.avg_trust)}</span>
                        <span>Cred: {t.avg_credibility?.toFixed(3)}</span>
                        {t.negative_credibility > 0 && <span className="text-danger">{t.negative_credibility} neg</span>}
                      </div>
                    ))}
                  </div>
                </>
              ) : <div className="db-empty-sm">Click to load trends</div>}
            </div>
          </Tab>

          {/* CALIBRATION */}
          <Tab eventKey="calibration" title={<><FaBalanceScale className="me-1" /> Calibration</>}>
            <div className="db-deep-content">
              {calibrationLoading ? <div className="db-loading-sm"><Spinner size="sm" variant="info" /> Loading…</div> : calibrationData ? (
                <>
                  <Row className="g-2 mb-3">
                    <Col xs={6} md={3}><MetricTile label="Total" value={calibrationData.overall?.total_calibrations || 0} icon={FaBalanceScale} color="#3b82f6" /></Col>
                    <Col xs={6} md={3}><MetricTile label="Avg Peers" value={calibrationData.overall?.avg_peers_evaluated?.toFixed(1) || '—'} icon={FaLayerGroup} color="#8b5cf6" /></Col>
                    <Col xs={6} md={3}><MetricTile label="Avg Cred Δ" value={calibrationData.overall?.avg_credibility_change?.toFixed(4) || '—'} icon={FaChartLine} color={(calibrationData.overall?.avg_credibility_change || 0) >= 0 ? '#22c55e' : '#ef4444'} /></Col>
                    <Col xs={6} md={3}><MetricTile label="With Conflicts" value={calibrationData.overall?.calibrations_with_conflicts || 0} icon={FaExclamationTriangle} color="#eab308" /></Col>
                  </Row>

                  {calibrationData.same_source_metrics && Object.keys(calibrationData.same_source_metrics).length > 0 && (
                    <div className="db-section">
                      <h6 className="db-deep-subtitle">Same-Source Discount Metrics</h6>
                      <div className="db-kv-grid">
                        {Object.entries(calibrationData.same_source_metrics).map(([k, v]) => (
                          <div key={k} className="db-kv-item"><span className="db-kv-key">{k.replace(/avg_|_/g, ' ').trim()}</span><span className="db-kv-val">{typeof v === 'number' ? v.toFixed(1) : v}</span></div>
                        ))}
                      </div>
                    </div>
                  )}

                  {calibrationData.pending && (
                    <div className="db-section">
                      <h6 className="db-deep-subtitle">Pending Calibrations</h6>
                      <div className="db-kv-grid">
                        <div className="db-kv-item"><span className="db-kv-key">Total Due</span><span className="db-kv-val" style={{ color: (calibrationData.pending.total_due || 0) > 50 ? '#ef4444' : undefined }}>{calibrationData.pending.total_due}</span></div>
                        <div className="db-kv-item"><span className="db-kv-key">Daily Tier</span><span className="db-kv-val">{calibrationData.pending.daily_tier}</span></div>
                        <div className="db-kv-item"><span className="db-kv-key">Weekly Tier</span><span className="db-kv-val">{calibrationData.pending.weekly_tier}</span></div>
                        <div className="db-kv-item"><span className="db-kv-key">Monthly Tier</span><span className="db-kv-val">{calibrationData.pending.monthly_tier}</span></div>
                      </div>
                    </div>
                  )}

                  <h6 className="db-deep-subtitle">Calibration Trend</h6>
                  <TrendBar data={calibrationData.trend} valueKey="calibrations" labelKey="period" height={100} />

                  {calibrationData.outliers && calibrationData.outliers.length > 0 && (
                    <div className="db-section">
                      <h6 className="db-deep-subtitle">Biggest Changes</h6>
                      {calibrationData.outliers.slice(0, 5).map((o, i) => (
                        <div key={i} className="db-outlier-row">
                          <span className="db-outlier-id"><FaFingerprint /> {o.learning_id?.slice(0, 8)}</span>
                          <span style={{ color: o.change >= 0 ? '#22c55e' : '#ef4444' }}>{o.cred_before?.toFixed(3)} → {o.cred_after?.toFixed(3)} ({o.change >= 0 ? '+' : ''}{o.change?.toFixed(3)})</span>
                          <Badge className="db-outlier-trigger">{o.trigger}</Badge>
                          <span>{o.peers} peers</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : <div className="db-empty-sm">Click to load calibration analytics</div>}
            </div>
          </Tab>

          {/* SOURCES */}
          <Tab eventKey="sources" title={<><FaSitemap className="me-1" /> Sources</>}>
            <div className="db-deep-content">
              {sourceLoading ? <div className="db-loading-sm"><Spinner size="sm" variant="info" /> Loading…</div> : sourceData ? (
                <>
                  {sourceData.trust_distribution && (
                    <div className="db-section">
                      <h6 className="db-deep-subtitle">Trust Distribution</h6>
                      <div className="db-dist-bars">
                        {sourceData.trust_distribution.map((b, i) => (
                          <MiniBar key={i} label={b.bucket} value={b.count} max={Math.max(...sourceData.trust_distribution.map(x => x.count), 1)} color="#22c55e" />
                        ))}
                      </div>
                    </div>
                  )}

                  {sourceData.by_type && (
                    <div className="db-section">
                      <h6 className="db-deep-subtitle">By Type</h6>
                      <div className="db-source-types">
                        {sourceData.by_type.map((t, i) => (
                          <div key={i} className="db-source-type-card">
                            <div className="db-source-type-name">{t.type}</div>
                            <div className="db-source-type-count">{t.count} sources</div>
                            <div className="db-source-type-trust">Trust: {formatPct(t.avg_trust)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {sourceData.sources && sourceData.sources.length > 0 && (
                    <div className="db-section">
                      <h6 className="db-deep-subtitle">All Sources (Ranked by Trust)</h6>
                      <div className="db-source-list">
                        {sourceData.sources.slice(0, 20).map((s, i) => (
                          <div key={i} className="db-source-row">
                            <div className="db-source-main">
                              <span className="db-source-name">{s.display_name || s.identifier}</span>
                              <Badge className="db-source-type">{s.type}</Badge>
                            </div>
                            <div className="db-source-stats">
                              <span style={{ color: scoreColor(s.trust) }}>Trust: {formatPct(s.trust)}</span>
                              <span>{s.active_learnings} learnings</span>
                              <span>Cred: {s.avg_credibility?.toFixed(3)}</span>
                              <span>Conf: {formatPct(s.confirmation_rate)}</span>
                              {s.negative_credibility > 0 && <span className="text-danger">{s.negative_credibility} neg</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : <div className="db-empty-sm">Click to load source analytics</div>}
            </div>
          </Tab>

          {/* SCORE EVOLUTION */}
          <Tab eventKey="scores" title={<><FaChartLine className="me-1" /> Scores</>}>
            <div className="db-deep-content">
              {scoreEvoLoading ? <div className="db-loading-sm"><Spinner size="sm" variant="info" /> Loading…</div> : scoreEvoData ? (
                <>
                  {scoreEvoData.distributions && (
                    <Row className="g-3 mb-3">
                      <Col md={6}>
                        <h6 className="db-deep-subtitle">Credibility Distribution</h6>
                        <div className="db-dist-bars">
                          {Object.entries(scoreEvoData.distributions.credibility || {}).map(([k, v], i) => (
                            <MiniBar key={i} label={k} value={v} max={Math.max(...Object.values(scoreEvoData.distributions.credibility || {}), 1)} color={k.includes('neg') ? '#ef4444' : '#3b82f6'} />
                          ))}
                        </div>
                      </Col>
                      <Col md={6}>
                        <h6 className="db-deep-subtitle">Importance Distribution</h6>
                        <div className="db-dist-bars">
                          {Object.entries(scoreEvoData.distributions.importance || {}).map(([k, v], i) => (
                            <MiniBar key={i} label={k} value={v} max={Math.max(...Object.values(scoreEvoData.distributions.importance || {}), 1)} color="#eab308" />
                          ))}
                        </div>
                      </Col>
                    </Row>
                  )}

                  {scoreEvoData.by_cohort && scoreEvoData.by_cohort.length > 0 && (
                    <div className="db-section">
                      <h6 className="db-deep-subtitle">Score Evolution by Cohort</h6>
                      <div className="db-cohort-list">
                        {scoreEvoData.by_cohort.map((c, i) => (
                          <div key={i} className="db-cohort-row">
                            <span className="db-cohort-date">{formatDate(c.cohort)}</span>
                            <span><strong>{c.count}</strong> learnings</span>
                            <span>Trust: {formatPct(c.avg_trust)}</span>
                            <span>Cred: {c.avg_credibility?.toFixed(3)}</span>
                            <span>Imp: {formatPct(c.avg_importance)}</span>
                            {c.negative_credibility > 0 && <span className="text-danger">{c.negative_credibility} neg</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : <div className="db-empty-sm">Click to load score evolution</div>}
            </div>
          </Tab>

          {/* CLASSIFICATIONS */}
          <Tab eventKey="classifications" title={<><FaTags className="me-1" /> Classes</>}>
            <div className="db-deep-content">
              {classLoading ? <div className="db-loading-sm"><Spinner size="sm" variant="info" /> Loading…</div> : classData ? (
                <>
                  {classData.totals && (
                    <div className="db-section">
                      <h6 className="db-deep-subtitle">Classification Totals</h6>
                      {classData.totals.map((t, i) => (
                        <div key={i} className="db-class-total-row">
                          <span className="db-class-total-name">{t.classification}</span>
                          <span><strong>{t.count}</strong></span>
                          <span>Cred: {t.avg_credibility?.toFixed(3)}</span>
                          <span>Trust: {formatPct(t.avg_trust)}</span>
                          <span>Calibrated: {t.calibrated_count}</span>
                          {t.negative_credibility > 0 && <span className="text-danger">{t.negative_credibility} neg</span>}
                        </div>
                      ))}
                    </div>
                  )}

                  {classData.trust_impact_by_classification && classData.trust_impact_by_classification.length > 0 && (
                    <div className="db-section">
                      <h6 className="db-deep-subtitle">Trust Impact by Classification</h6>
                      {classData.trust_impact_by_classification.map((t, i) => (
                        <div key={i} className="db-trust-impact-row">
                          <span className="db-trust-impact-name">{t.classification}</span>
                          <span className="text-success">+{t.avg_positive_adj?.toFixed(6)} ({t.positive_count})</span>
                          <span className="text-danger">{t.avg_negative_adj?.toFixed(6)} ({t.negative_count})</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : <div className="db-empty-sm">Click to load classifications</div>}
            </div>
          </Tab>

          {/* NODES */}
          <Tab eventKey="nodes" title={<><FaExchangeAlt className="me-1" /> Nodes</>}>
            <div className="db-deep-content">
              {nodeLoading ? <div className="db-loading-sm"><Spinner size="sm" variant="info" /> Loading…</div> : nodeData ? (
                <>
                  <Row className="g-2 mb-3">
                    <Col xs={6} md={2}><MetricTile label="Total Nodes" value={nodeData.overall?.total_nodes || 0} icon={FaExchangeAlt} color="#3b82f6" /></Col>
                    <Col xs={6} md={2}><MetricTile label="Supporting" value={nodeData.overall?.supporting || 0} icon={FaCheckCircle} color="#22c55e" /></Col>
                    <Col xs={6} md={2}><MetricTile label="Contradictory" value={nodeData.overall?.contradictory || 0} icon={FaTimesCircle} color="#ef4444" /></Col>
                    <Col xs={6} md={2}><MetricTile label="Avg Strength" value={nodeData.overall?.avg_strength?.toFixed(3) || '—'} icon={FaChartLine} color="#8b5cf6" /></Col>
                    <Col xs={6} md={2}><MetricTile label="Avg Confidence" value={formatPct(nodeData.overall?.avg_confidence)} icon={FaShieldAlt} color="#eab308" /></Col>
                  </Row>

                  {nodeData.by_type && (
                    <div className="db-section">
                      <h6 className="db-deep-subtitle">By Connection Type</h6>
                      {nodeData.by_type.map((t, i) => (
                        <div key={i} className="db-node-type-row">
                          <span className="db-node-type-name">{t.type}</span>
                          <strong>{t.count}</strong>
                          <span>Str: {t.avg_strength?.toFixed(3)}</span>
                          <span>Conf: {formatPct(t.avg_confidence)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {nodeData.top_connected && nodeData.top_connected.length > 0 && (
                    <div className="db-section">
                      <h6 className="db-deep-subtitle">Most Connected Learnings</h6>
                      {nodeData.top_connected.map((n, i) => (
                        <div key={i} className="db-connected-row">
                          <Badge className="db-connected-count">{n.connections}</Badge>
                          <span className="db-connected-preview">{n.preview}</span>
                          <Badge className="db-type-badge">{n.type}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : <div className="db-empty-sm">Click to load node evolution</div>}
            </div>
          </Tab>

          {/* GROWTH */}
          <Tab eventKey="growth" title={<><FaArrowUp className="me-1" /> Growth</>}>
            <div className="db-deep-content">
              {growthLoading ? <div className="db-loading-sm"><Spinner size="sm" variant="info" /> Loading…</div> : growthData ? (
                <>
                  <Row className="g-2 mb-3">
                    <Col xs={6} md={4}><MetricTile label="Avg Weekly" value={growthData.avg_weekly?.toFixed(1) || 0} icon={FaChartLine} color="#22c55e" /></Col>
                  </Row>
                  <h6 className="db-deep-subtitle">Historical Weekly Growth</h6>
                  <TrendBar data={growthData.historical} valueKey="count" labelKey="week" height={120} />

                  {growthData.projections && growthData.projections.length > 0 && (
                    <div className="db-section">
                      <h6 className="db-deep-subtitle">Projections (Next 4 Weeks)</h6>
                      <div className="db-projections">
                        {growthData.projections.map((p, i) => (
                          <div key={i} className="db-projection-item">
                            <span>{formatDate(p.week)}</span>
                            <strong>~{p.projected}</strong>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : <div className="db-empty-sm">Click to load growth projections</div>}
            </div>
          </Tab>

        </Tabs>
      </Card>
    </div>
  );
};

export default KnowledgeDashboard;
